// The cart's RBP missed-products widget (SL1.0 `www/engine/get_cart.php:955-1143`, RULE-036): the
// store's core products not bought this quarter or last, not in the cart, priced above zero, at
// most ten per department of the cart (every department for an empty cart). Figma has no frame
// for it, so it keeps SL1.0's frame on the design system's parts: a card titled as SL1.0 titles
// it, one closed-by-default collapsible group per department, and per row "<code>: <name>", the
// pack size and the product listing's green Add (the cart stepper's starting state, in place of
// SL1.0's outline Add, `:1097-1113`), listing only products the supplier lets the store order at
// the depot (`useMissedProducts`). Add puts one into the current cart through the
// shared add confirmation (`useAddConfirmation`), so a conflict, the "Added to cart" toast and the
// totals behave as a listing add does (Story 18.19, AC8), with no upsell; it is a plain add, with
// no RBP attribution (SL1.0's `AddProductAndReloadCart`). The
// row leaves the list once the cart holds the product: focus then moves to the next row's Add (the
// previous one's when it was the last row of its department, else the card's heading, else the page
// title when the card leaves) and a polite live region, mounted for the card's whole life, says
// what was added. Drawn only when there is something to show; the cart's side
// panel never draws it (SL1.0 shows it on the view-cart page only). Story 18.19 (ADO-4086): the
// card is the Order Review's sidebar, a complementary landmark named by its heading; the page
// places it (beside the main review from 1200px, after it below that) through `className`.

import { useEffect, useId, useRef, useState } from 'react';
import { Button, Collapse, Icon } from '../../design-system';
import { packCaseLabel } from '../../basket/packCase';
import { useAddConfirmation } from '../../basket/useAddConfirmation';
import { useBasketStore } from '../../basket/useBasketStore';
import type { MissedProductGroup } from '../../rbp/missedProducts';
import { useMissedProducts, type CartFacts } from '../../rbp/useMissedProducts';
import styles from './RbpMissedProductsCard.module.css';

/** SL1.0's title for the widget (`get_cart.php:1041`). */
export const RBP_MISSED_PRODUCTS_TITLE = 'RBP products you may have missed';

/** The heading a product with no department groups under. */
const NO_DEPARTMENT = 'No department';

/** The pause between clearing the live region and saying the new message. */
export const ANNOUNCE_DELAY_MS = 50;

/** How long a said message stays in the live region before it is cleared. */
export const ANNOUNCE_HOLD_MS = 3000;

export interface RbpMissedProductsCardProps extends CartFacts {
  /** The page's own sizing for the card, joined onto its class. */
  readonly className?: string;
}

export function RbpMissedProductsCard({ className, ...cart }: RbpMissedProductsCardProps) {
  const { groups } = useMissedProducts(cart);
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [announcement, setAnnouncement] = useState('');
  const mounted = useRef(true);
  const sayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The codes added since the region was last spoken: adds landing within the delay are said once,
  // together, so none is dropped.
  const pendingCodes = useRef<string[]>([]);
  // Set by a landed add, until the next commit checks whether the card left with it.
  const lastAdded = useRef<string | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (sayTimer.current !== null) clearTimeout(sayTimer.current);
      if (clearTimer.current !== null) clearTimeout(clearTimer.current);
    };
  }, []);

  // The card left with the product just added (an add takes its row off the page before it
  // resolves): the focus it held goes to the page title. Checked once, on the commit after the add,
  // so a card emptied later for another reason moves no focus.
  const empty = groups.length === 0;
  useEffect(() => {
    if (lastAdded.current === null) return;
    lastAdded.current = null;
    if (empty && document.activeElement === document.body) {
      document.getElementById('cart-page-title')?.focus();
    }
  });

  const say = () => {
    sayTimer.current = null;
    const codes = pendingCodes.current;
    pendingCodes.current = [];
    setAnnouncement(`${codes.join(', ')} added to your cart`);
    if (clearTimer.current !== null) clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => setAnnouncement(''), ANNOUNCE_HOLD_MS);
  };

  // Move focus off a recommendation that has just left the list: to the neighbour Add captured
  // before it went while that is still on the page, else the card's heading, else the page title
  // when the card left with its last row. The page-title fallback only claims focus the body holds,
  // so it never pulls focus from wherever it now sits.
  const recoverFocus = (neighbour: HTMLElement | null) => {
    const target = neighbour?.isConnected === true ? neighbour : headingRef.current;
    if (target?.isConnected === true) {
      target.focus();
    } else if (document.activeElement === document.body) {
      document.getElementById('cart-page-title')?.focus();
    }
  };

  // A recommendation leaving the list after its Add was used: recover the focus its row held. A
  // landed add also arms the page-title fallback for when the card empties on the announcement
  // commit and announces what was added (the region is cleared first, so the same words twice still
  // read as a change). A refused conflict added nothing: it announces nothing, raises no toast (the
  // shared confirmation suppressed both) and leaves no `lastAdded` marker, which schedules no commit
  // and so would only steal a later empty render's focus.
  const onRowResolved = (productCode: string, neighbour: HTMLElement | null, landed: boolean) => {
    if (!mounted.current) return;
    recoverFocus(neighbour);
    if (!landed) return;
    lastAdded.current = productCode;
    pendingCodes.current.push(productCode);
    if (sayTimer.current !== null) return;
    if (clearTimer.current !== null) clearTimeout(clearTimer.current);
    setAnnouncement('');
    sayTimer.current = setTimeout(say, ANNOUNCE_DELAY_MS);
  };

  // One region for the card's whole life, the empty state included, so the add that empties the
  // card is still announced in a region the screen reader already knows.
  const liveRegion = (
    <p role="status" aria-live="polite" className={styles.visuallyHidden}>
      {announcement}
    </p>
  );
  return (
    <>
      {empty ? null : (
        <aside
          className={className === undefined ? styles.card : `${styles.card} ${className}`}
          aria-labelledby={headingId}
        >
          <h2 id={headingId} ref={headingRef} tabIndex={-1} className={styles.title}>
            {RBP_MISSED_PRODUCTS_TITLE}
          </h2>
          {groups.map((group) => (
            <DepartmentGroup
              key={group.departmentName ?? ''}
              group={group}
              onRowResolved={onRowResolved}
            />
          ))}
        </aside>
      )}
      {liveRegion}
    </>
  );
}

type OnRowResolved = (productCode: string, neighbour: HTMLElement | null, landed: boolean) => void;

function DepartmentGroup({
  group,
  onRowResolved,
}: {
  readonly group: MissedProductGroup;
  readonly onRowResolved: OnRowResolved;
}) {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const name = group.departmentName ?? NO_DEPARTMENT;
  return (
    <section className={styles.group} aria-label={name}>
      <button
        type="button"
        className={styles.groupToggle}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{name}</span>
        <Icon name={open ? 'caretDown' : 'caretRight'} size={18} />
      </button>
      <Collapse id={contentId} open={open}>
        <ul className={styles.rows}>
          {group.products.map((product) => (
            <MissedProductRow
              key={product.productCode}
              {...product}
              onRowResolved={onRowResolved}
            />
          ))}
        </ul>
      </Collapse>
    </section>
  );
}

function MissedProductRow({
  productCode,
  name,
  packSize,
  onRowResolved,
}: {
  readonly productCode: string;
  readonly name: string | null;
  readonly packSize: string | null;
  readonly onRowResolved: OnRowResolved;
}) {
  const error = useBasketStore((s) => s.mutationErrors.get(productCode));
  const rowRef = useRef<HTMLLIElement>(null);
  const label = name === null ? productCode : `${productCode}: ${name}`;
  const pack = packCaseLabel(null, packSize);
  const { confirm } = useAddConfirmation(productCode, {
    productName: name ?? productCode,
    upsellVariant: 'toast',
    upsell: false,
  });
  const add = async () => {
    // Read before the add: an optimistic add can take this row off the page before it resolves.
    const row = rowRef.current;
    const trigger = row?.querySelector<HTMLElement>('button') ?? null;
    const neighbour =
      row?.nextElementSibling?.querySelector<HTMLElement>('button') ??
      row?.previousElementSibling?.querySelector<HTMLElement>('button') ??
      null;
    if (await confirm(1)) {
      // A landed add: move focus on and announce.
      onRowResolved(productCode, neighbour, true);
      return;
    }
    // A refused add added nothing, so it never announces. A line conflict, though, refreshes the
    // basket with the server's; when that now holds this product the recommendation has left the
    // list with its focused Add. Recover focus only for that case (a plain mutation error, even with
    // a line present, is not a conflict and keeps its row and `role="alert"`), and only when this
    // add's own control still owns focus or it fell to the body as its row left: if the user moved
    // focus elsewhere while the add was in flight, leave it where they put it.
    const settled = useBasketStore.getState();
    const refreshedIntoCart =
      settled.lineConflicts.has(productCode) &&
      settled.lines.some((line) => line.skuCode === productCode);
    const ownsFocus =
      document.activeElement === trigger || document.activeElement === document.body;
    if (refreshedIntoCart && ownsFocus) onRowResolved(productCode, neighbour, false);
  };
  return (
    <li className={styles.row} ref={rowRef}>
      <span className={styles.product}>
        <span className={styles.name}>{label}</span>
        {pack !== null ? <span className={styles.pack}>{pack}</span> : null}
        {error !== undefined ? (
          <span className={styles.error} role="alert">
            {error}
          </span>
        ) : null}
      </span>
      <Button
        variant="primary"
        className={styles.add}
        data-control="add"
        aria-label={`Add ${label}`}
        onClick={() => void add()}
      >
        Add
      </Button>
    </li>
  );
}
